/* Grubwick admin — reusable UI primitives: fields, modal, confirm, menu-item editor, toasts. */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import type { VenueType } from "../../../data/adminData";
import { CATEGORIES, type MenuItemRecord } from "../../../data/adminData";
import { VENUE_META, X, Check, Kebab, Grip, Spinner } from "../icons/icons";

/* Sentinel option for "no category" — selecting it stores an empty category,
   which the menu table renders as no chip at all. */
const NO_CATEGORY = "None";

/* ── venue tag ── */
export function VenueTag({
  venue,
  size,
}: {
  venue: VenueType;
  size?: number;
}): React.ReactElement {
  const m = VENUE_META[venue] || VENUE_META["Restaurant"];
  const Ic = m.Icon;
  return (
    <span
      className="vtag"
      style={{
        color: m.deep,
        background:
          "color-mix(in oklab, " + m.color + " var(--tag-mix), transparent)",
      }}
    >
      <Ic size={size || 12} /> {venue}
    </span>
  );
}

/* ── form fields ── */
export function Field({
  label,
  hint,
  children,
  span,
}: {
  label?: string;
  hint?: string;
  children: React.ReactNode;
  span?: boolean;
}): React.ReactElement {
  return (
    <div className={"field" + (span ? " col-span" : "")}>
      {label && (
        <label>
          {label}
          {hint && (
            <span
              className="hint"
              style={{ marginLeft: "auto", fontWeight: 500 }}
            >
              {hint}
            </span>
          )}
        </label>
      )}
      {children}
    </div>
  );
}

export function Input({
  value,
  onChange,
  placeholder,
  type,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <input
      className="inp"
      type={type || "text"}
      value={value || ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function PriceInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): React.ReactElement {
  // value stored WITHOUT leading $ in the field, normalized on the way out
  const shown = (value || "").replace(/^\$/, "");
  return (
    <div className="inp-prefix">
      <span className="pfx">$</span>
      <input
        className="inp"
        value={shown}
        placeholder={placeholder || "0"}
        inputMode="decimal"
        onChange={(e) => onChange(e.target.value.replace(/^\$/, ""))}
      />
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  disabled?: boolean;
}): React.ReactElement {
  return (
    <select
      className="sel"
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <textarea
      className="ta"
      rows={rows || 3}
      value={value || ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/* ── modal shell ── */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}): React.ReactElement {
  useEffect(() => {
    function esc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div
      className="modal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-h">
          <h3>{title}</h3>
          <button className="x-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="modal-b">{children}</div>
        {footer && <div className="modal-f">{footer}</div>}
      </div>
    </div>
  );
}

/* ── confirm dialog ──
   `requireText` adds a type-to-confirm gate (the button stays disabled until the
   user types that word, case-insensitive) for high-consequence actions. `busy`
   disables both buttons and shows a spinner while an async action is in flight;
   pass `autoClose={false}` so the caller keeps the modal open during that work
   and dismisses it itself on success/error. */
export function Confirm({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  danger,
  requireText,
  busy,
  autoClose = true,
}: {
  title: string;
  body: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  danger?: boolean;
  requireText?: string;
  busy?: boolean;
  autoClose?: boolean;
}): React.ReactElement {
  const [typed, setTyped] = useState("");
  const textOk =
    !requireText || typed.trim().toLowerCase() === requireText.toLowerCase();
  const disabled = busy || !textOk;
  return (
    <Modal
      title={title}
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className={"btn " + (danger ? "btn-danger" : "btn-primary")}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              onConfirm();
              if (autoClose) onClose();
            }}
          >
            {busy ? "Working…" : confirmLabel || "Confirm"}
          </button>
        </>
      }
    >
      <p
        style={{
          margin: 0,
          color: "var(--ink-soft)",
          fontSize: 14.5,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {requireText && (
        <div style={{ marginTop: 16 }}>
          <Field label={`Type “${requireText}” to confirm`}>
            <Input value={typed} onChange={setTyped} placeholder={requireText} />
          </Field>
        </div>
      )}
    </Modal>
  );
}

/* ── kebab "more actions" menu ──
   A small overflow menu for secondary/destructive actions that shouldn't take
   up permanent space (e.g. Delete). Closes on outside click or Escape. Items
   can be flagged `danger` to render in the maroon destructive style. */
export interface KebabItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
}
export function KebabMenu({
  items,
  label = "More actions",
}: {
  items: KebabItem[];
  label?: string;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node))
        setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  return (
    <div className="kebab" ref={wrap}>
      <button
        className="kebab-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Kebab size={18} />
      </button>
      {open && (
        <div className="kebab-menu" role="menu">
          {items.map((it) => (
            <button
              key={it.label}
              className={"kebab-item" + (it.danger ? " danger" : "")}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── menu item editor modal ── */
export function MenuItemModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: MenuItemRecord;
  onSave: (item: MenuItemRecord) => void;
  onClose: () => void;
}): React.ReactElement {
  // Normalize the "—" placeholder (used for an unknown extracted price) to an
  // empty field so the user types into a blank, not over a dash.
  const [it, setIt] = useState<Partial<MenuItemRecord>>(
    initial
      ? { ...initial, price: initial.price === "—" ? "" : initial.price }
      : { name: "", price: "", category: "", desc: "", sectionId: null },
  );
  const set = <K extends keyof MenuItemRecord>(k: K, v: MenuItemRecord[K]) =>
    setIt((s) => ({ ...s, [k]: v }));
  const valid = (it.name || "").trim().length > 0;
  function save() {
    if (!valid) return;
    const price = it.price ? "$" + String(it.price).replace(/^\$/, "") : "";
    onSave({ ...(it as MenuItemRecord), name: (it.name || "").trim(), price });
  }
  return (
    <Modal
      title={initial && initial.id ? "Edit item" : "New menu item"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={!valid}>
            <Check size={16} /> Save item
          </button>
        </>
      }
    >
      <Field label="Item name">
        <Input
          value={it.name || ""}
          onChange={(v) => set("name", v)}
          placeholder="e.g. Soy-Garlic Double-Fried"
        />
      </Field>
      <div className="fgrid two">
        <Field label="Price">
          <PriceInput
            value={it.price || ""}
            onChange={(v) => set("price", v)}
            placeholder="14"
          />
        </Field>
        <Field label="Category">
          <Select
            value={it.category || NO_CATEGORY}
            onChange={(v) => set("category", v === NO_CATEGORY ? "" : v)}
            options={[NO_CATEGORY, ...CATEGORIES]}
          />
        </Field>
      </div>
      <Field label="Description" hint="optional">
        <TextArea
          value={it.desc || ""}
          onChange={(v) => set("desc", v)}
          placeholder="A short, appetizing line about the dish."
        />
      </Field>
    </Modal>
  );
}

/* ── section reorder modal ──
   A compact drag-to-reorder list of menu sections (dnd-kit/sortable, so it has
   touch + keyboard support out of the box). The uncategorized "Other" bucket, if
   present, is shown pinned and non-draggable at the bottom and is never part of
   the saved order. `busy` disables the controls and shows a spinner while the
   reorder request is in flight; the caller closes the modal on success. */
export interface ReorderSection {
  id: string;
  label: string;
  count: number;
}

function SortableSectionRow({
  section,
}: {
  section: ReorderSection;
}): React.ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={"reorder-row" + (isDragging ? " is-dragging" : "")}
    >
      <button
        type="button"
        className="reorder-grip"
        aria-label={`Drag ${section.label}`}
        {...attributes}
        {...listeners}
      >
        <Grip size={16} />
      </button>
      <span className="reorder-label">{section.label}</span>
      <span className="reorder-n">{section.count}</span>
    </li>
  );
}

export function SectionReorderModal({
  sections,
  uncategorized,
  busy,
  onSave,
  onClose,
}: {
  sections: ReorderSection[];
  uncategorized?: ReorderSection | null;
  busy?: boolean;
  onSave: (orderedIds: string[]) => void;
  onClose: () => void;
}): React.ReactElement {
  const [order, setOrder] = useState<ReorderSection[]>(sections);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = order.map((s) => s.id);
  const changed =
    ids.length !== sections.length || ids.some((id, i) => id !== sections[i].id);

  function onDragEnd(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setOrder((prev) => {
      const from = prev.findIndex((s) => s.id === active.id);
      const to = prev.findIndex((s) => s.id === over.id);
      return from === -1 || to === -1 ? prev : arrayMove(prev, from, to);
    });
  }

  return (
    <Modal
      title="Reorder sections"
      onClose={() => {
        if (!busy) onClose();
      }}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (busy || !changed) return;
              onSave(ids);
            }}
            disabled={busy || !changed}
          >
            {busy ? (
              <span className="spin">
                <Spinner size={16} />
              </span>
            ) : (
              <Check size={16} />
            )}{" "}
            Save order
          </button>
        </>
      }
    >
      <p
        style={{
          margin: 0,
          color: "var(--ink-soft)",
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        Drag sections into the order they should appear on the menu.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        // The list scrolls inside itself. dnd-kit's layout-shift compensation
        // applies a counter-transform as the container auto-scrolls, which fights
        // the scroll here and leaves it stuck just short of the top edge when you
        // drag back up. Turn it off so the container can scroll fully to the top.
        autoScroll={{ layoutShiftCompensation: false }}
        // Lock dragging to the vertical axis and keep the row inside the list so
        // it can't be flung out of bounds sideways or past the ends.
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ul className="reorder-list is-sortable">
            {order.map((s) => (
              <SortableSectionRow key={s.id} section={s} />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {uncategorized && (
        <ul className="reorder-list">
          <li className="reorder-row is-pinned">
            <span className="reorder-grip is-disabled" aria-hidden="true">
              <Grip size={16} />
            </span>
            <span className="reorder-label">{uncategorized.label}</span>
            <span className="reorder-n">{uncategorized.count}</span>
            <span className="reorder-pin">Always last</span>
          </li>
        </ul>
      )}
    </Modal>
  );
}

/* ── toast system ── */
type ToastFn = (msg: string) => void;
const ToastCtx = createContext<ToastFn>(() => {});

export function ToastProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [toasts, setToasts] = useState<{ id: string; msg: string }[]>([]);
  const push: ToastFn = (msg) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2400);
  };
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-wrap">
        {toasts.map((t) => (
          <div className="toast" key={t.id}>
            <span className="tk">
              <Check size={15} />
            </span>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = (): ToastFn => useContext(ToastCtx);
